import { Injectable } from '@angular/core';
import { CoreListModalPage } from '@core-modules/components/core-list-modal/core-list-modal.modal-page';
import { FadeEnterTransition, FadeLeaveTransition } from '@core-modules/transitions/fade.transition';
import { LoadingController, ModalController } from '@ionic/angular';
import { CoreAlertPage, CoreDialogButton } from '../../components/core-alert/core-alert.component';

/*
  Generated class for the CoreDialogController provider.

  See https://angular.io/docs/ts/latest/guide/dependency-injection.html
  for more info on providers and Angular 2 DI.
*/
@Injectable()
export class CoreDialogController
{

    //#region Constructors

    /**
     * Class constructor
     * @param modalCtrl 
     */
    constructor
        (
            private modalCtrl: ModalController,
            private loadingController: LoadingController
        )
    {
        this.modals = [];
        this.loader = null;
    }


    //#endregion


    //#region Properties

    /**
     * stores any modals created except for loader
     */
    private modals: HTMLIonModalElement[];

    /**
     * Modal storing a single instance of a loader
     */
    private loader: any

    //#endregion


    //#region Methods

    /**
     * Create a Core Loader dialog
     * @param loadingText 
     */
    private async createCoreLoader(loadingText?: string)
    {
        this.loader = await this.loadingController.create({
            spinner: null,
            duration: null,
            mode: 'md',
            message: '<img #img class="loading-spinner" src="./assets/images/spinners/spinner.svg" /><div style="loading-text">'
                + (loadingText || 'Loading...') + '</div>',
            translucent: true,
            cssClass: 'core-ion-loading',
            backdropDismiss: false
        });

        return this.loader;
    }

    /**
     * Present the Core Loader dialog 
     * @param loadingText 
     */
    public async showLoader(loadingText?: string): Promise<any>
    {
        // checks if loader is already created
        if (this.loader == null)
        {
            await this.createCoreLoader(loadingText);
            await this.loader.present();
        }
    }

    /**
     * Dismiss the Core Loader dialog 
     * @param loader 
     */
    public async hideLoader(): Promise<any>
    {
        // checks if a loader is created
        if (this.loader != null)
        {
            await this.loader.dismiss();
            await this.removeLoader();
        }
    }

    /**
     * Remove loader method
     */
    private removeLoader()
    {
        this.loader = null;
    }

    /**
     * Create a Core Alert
     * @param title 
     * @param message 
     * @param buttons 
     */
    public async createCoreAlert(title: string, message: string, buttons: CoreDialogButton[])
    {
        let alert = await this.modalCtrl.create({
            component: CoreAlertPage,
            cssClass: 'core-modal core-alert',
            mode: 'md',
            enterAnimation: FadeEnterTransition,
            leaveAnimation: FadeLeaveTransition,
            animated: false,
            backdropDismiss: false,
            showBackdrop: true,
            componentProps: {
                'title': title,
                'message': message,
                'buttons': buttons
            }
        });

        // add the modal to the internal list of modals
        this.modals.push(alert);

        // when the modal is to be dismissed, remove it from the list of modals
        alert.onDidDismiss().then(() =>
        {
            this.modals = this.modals.filter(x => x !== alert);
        });

        return alert;
    }

    /**
     * Create a core themed modal
     * @param modalPage 
     * @param params 
     */
    public async createCoreModal(modalPage: any, params: any, cssClass: string = null): Promise<HTMLIonModalElement>
    {
        let cssClasses = 'core-modal';

        // if the user some cssClass to be added to the modal
        // add it to the cssClasses list
        if (cssClass !== null)
        {
            cssClasses += ' ' + cssClass;
        }

        let modalOptions = {
            component: modalPage,
            cssClass: cssClasses,
            enterAnimation: FadeEnterTransition,
            leaveAnimation: FadeLeaveTransition,
            mode: 'md',
            animated: true,
            backdropDismiss: false,
            showBackdrop: true,
            componentProps: params
        };

        //IonicUpdate modal create will take a modaloptions object wre the modal and its options will reside
        let modal = await this.modalCtrl.create(<any>modalOptions);

        // add the modal to the internal list of modals
        this.modals.push(modal);

        // when the modal is to be dismissed, remove it from the list of modals
        modal.onDidDismiss().then(() =>
        {
            this.modals = this.modals.filter(x => x !== modal);
        });

        return modal;
    }

    /**
     * Create a Core Select Dialog
     * @param modalTitle Title of the modal
     * @param options Options to show
     */
    public async createCoreListDialog(modalTitle: string, options: any[]): Promise<HTMLIonModalElement>
    {
        let modal = await this.modalCtrl.create({
            component: CoreListModalPage,
            enterAnimation: FadeEnterTransition,
            leaveAnimation: FadeLeaveTransition,
            mode: 'md',
            componentProps:
            {
                modalTitle: modalTitle,
                options: options
            },
            cssClass: 'core-modal'
        });

        // add the modal to the internal list of modals
        this.modals.push(modal);

        // when the modal is to be dismissed, remove it from the list of modals
        modal.onDidDismiss().then(() =>
        {
            this.modals = this.modals.filter(x => x !== modal);
        });

        return modal;
    }

    /**
     * Show the feature not supported dialog
     */
    public async showFeatureNotSupportedDialog(): Promise<void>
    {
        return new Promise(async (resolve) =>
        {
            // prepare a list of buttons
            let buttons: CoreDialogButton[] = [{
                text: 'OK',
                solid: true,
                action: () =>
                {
                    // resolve the promise once the user presses ok
                    resolve();
                }
            }];

            let alert = await this.createCoreAlert('Feature Unavailable', 'This functionality is not available for this account.', buttons);
            alert.present();
        });
    }

    /**
     * Close all of the currently open modals
     */
    public closeAll()
    {
        // dismiss loader
        this.hideLoader();
        this.modals.forEach(async x =>
        {
            try
            {
                // dismiss any modals remaining
                x.dismiss();
            } catch {
                // catch potential error if loader being presented / initialized
                // use Promise.resolve to handle await calls to this method as it can cause infinite loop
                return Promise.resolve();
            }
        });
    }

    //#endregion
}